import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  UserPlus, Mail, KeyRound, Trash2, Copy, Link as LinkIcon,
  ShieldCheck, AlertTriangle, Loader2, X, Send,
} from "lucide-react";
import { copyToClipboard } from "@/lib/clipboard";

export default function AdminTeamPage() {
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [twilioConfigured, setTwilio] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(null);  // { kind, url, email, expires_at }

  const load = async () => {
    setLoading(true);
    try {
      const [u, inv, st] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/invites"),
        api.get("/auth/2fa/status"),
      ]);
      setAdmins(u.data || []);
      setInvites(inv.data || []);
      setTwilio(!!st.data?.twilio_configured);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  // Mount-only initial load — subsequent refreshes are user-triggered.
  useEffect(() => { load(); }, []);

  const toggle2fa = async (u) => {
    const next = !u.twofa_enabled;
    try {
      await api.patch(`/users/${u.user_id}/2fa`, { twofa_enabled: next });
      toast.success(next ? "2FA on for " + u.name : "2FA off for " + u.name);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const resetLink = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.user_id}/password-reset-link`);
      setLinkOpen({
        kind: "reset",
        url: data.reset_url,
        email: data.email,
        expires_at: data.expires_at,
        name: u.name,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const revokeInvite = async (token) => {
    try {
      await api.delete(`/admin/invites/${token}`);
      toast.success("Invite revoked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Platform access</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            Admin team
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Invite Iceberg Digital admins, send password reset links, and
            toggle SMS 2FA per account.
          </p>
        </div>
        <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen}
          onCreated={(payload) => {
            setInviteOpen(false);
            setLinkOpen({
              kind: "invite",
              url: payload.accept_url,
              email: payload.email,
              expires_at: payload.expires_at,
            });
            load();
          }} />
      </div>

      {/* 2FA dormant banner */}
      {!twilioConfigured && (
        <Card className="rounded-sm border-amber-300 bg-amber-50" data-testid="twofa-dormant-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-display font-bold">2FA toggles are dormant.</div>
              <div className="text-zinc-700">
                Twilio credentials are not configured yet, so login proceeds
                without an SMS code even when 2FA is on. Set
                <code className="mx-1 font-mono bg-white px-1 rounded">TWILIO_ACCOUNT_SID</code>,
                <code className="mx-1 font-mono bg-white px-1 rounded">TWILIO_AUTH_TOKEN</code> and
                <code className="mx-1 font-mono bg-white px-1 rounded">TWILIO_FROM_NUMBER</code>
                in <code className="font-mono bg-white px-1 rounded">backend/.env</code> to activate.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending invites */}
      {invites.length > 0 && (
        <div>
          <div className="label-overline mb-3">Pending invites</div>
          <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-200 bg-white">
            {invites.map((inv) => (
              <div key={inv.token} className="flex items-center gap-4 px-4 py-3"
                data-testid={`invite-row-${inv.token}`}>
                <div className="h-9 w-9 bg-amber-100 text-amber-800 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Mail className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">
                    {[inv.first_name, inv.last_name].filter(Boolean).join(" ") || inv.email}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {inv.email} · expires {new Date(inv.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-sm"
                  onClick={() => setLinkOpen({
                    kind: "invite", url: inv.accept_url,
                    email: inv.email, expires_at: inv.expires_at,
                  })}
                  data-testid={`invite-copy-${inv.token}`}>
                  <LinkIcon className="h-3.5 w-3.5 mr-1.5" /> Copy link
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
                      data-testid={`invite-revoke-${inv.token}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-sm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Revoke invite for {inv.email}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        The accept link will stop working immediately.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700"
                        onClick={() => revokeInvite(inv.token)}
                        data-testid={`invite-confirm-revoke-${inv.token}`}>
                        Revoke
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active admins */}
      <div>
        <div className="label-overline mb-3">Active admins</div>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-200 bg-white">
          {admins.map((u) => (
            <div key={u.user_id} className="flex items-center gap-4 px-4 py-3 hover:bg-zinc-50"
              data-testid={`admin-row-${u.user_id}`}>
              <div className="h-9 w-9 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-display font-bold flex-shrink-0">
                {(u.name || u.email || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-sm">{u.name || u.email}</span>
                  {u.twofa_enabled && (
                    <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">
                      <ShieldCheck className="h-3 w-3 mr-1" /> 2FA on
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-500 truncate">{u.email}{u.phone ? " · " + u.phone : ""}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-2 mr-3">
                  <Switch checked={!!u.twofa_enabled}
                    onCheckedChange={() => toggle2fa(u)}
                    data-testid={`admin-2fa-toggle-${u.user_id}`} />
                  <Label className="text-xs label-overline">2FA</Label>
                </div>
                <Button size="sm" variant="outline" className="rounded-sm"
                  onClick={() => resetLink(u)}
                  data-testid={`admin-reset-link-${u.user_id}`}>
                  <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Reset link
                </Button>
              </div>
            </div>
          ))}
          {!loading && admins.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">
              No admin users yet.
            </div>
          )}
        </div>
      </div>

      {/* Shareable link modal (invite or reset) */}
      <Dialog open={!!linkOpen} onOpenChange={(o) => !o && setLinkOpen(null)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="link-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">
              {linkOpen?.kind === "invite" ? "Admin invite link" : "Password reset link"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">
            Send the link below to{" "}
            <strong>{linkOpen?.email || linkOpen?.name}</strong>.
            It expires on{" "}
            {linkOpen?.expires_at && (
              <strong>{new Date(linkOpen.expires_at).toLocaleString()}</strong>
            )}.
          </p>
          <div className="flex gap-2">
            <Input value={linkOpen?.url || ""} readOnly className="rounded-sm font-mono text-xs"
              data-testid="link-share-url" />
            <Button onClick={async () => {
              const ok = await copyToClipboard(linkOpen?.url || "");
              toast[ok ? "success" : "error"](ok ? "Link copied" : "Couldn't copy — select & copy manually");
            }} className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="link-share-copy">
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-sm" onClick={() => setLinkOpen(null)}>
              <X className="h-3.5 w-3.5 mr-1" /> Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InviteDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", form);
      toast.success("Invite generated — copy the link to share");
      setForm({ email: "", first_name: "", last_name: "" });
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="rounded-sm bg-zinc-900 hover:bg-black text-white h-10"
          data-testid="invite-admin-button">
          <UserPlus className="h-4 w-4 mr-1.5" /> Invite admin
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-md" data-testid="invite-admin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold">Invite a new admin</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-500">
          We&apos;ll generate a one-time link (valid for 7 days). Copy it to share — the
          invitee will set their own password.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="label-overline">Email <span className="text-red-600 ml-0.5">*</span></Label>
            <Input type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="name@icebergdigital.com.au" className="rounded-sm h-10"
              data-testid="invite-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="label-overline">First name</Label>
              <Input value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="rounded-sm h-10" data-testid="invite-first-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Last name</Label>
              <Input value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="rounded-sm h-10" data-testid="invite-last-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy || !form.email}
              className="rounded-sm bg-zinc-900 hover:bg-black text-white"
              data-testid="invite-submit-button">
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {busy ? "Generating…" : "Generate invite link"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
